<template>
	<page-container :page="page">
		<heading-image
			v-if="page.headingImage"
			:heading-image="{
				image: page.headingImage,
				imageWebp: page.headingImageWebp,
				imagePortrait: page.headingImagePortrait,
				imagePortraitWebp: page.headingImagePortraitWebp,
			}"
		/>
		<breadcrumbs :breadcrumbs="page.breadcrumbs" :class="page.headingImage ? '' : 'no-slider'" />

		<section id="intro" class="main-content intro">
			<div class="row">
				<div class="columns column6">
					<h1>{{ page.title || page.header }}</h1>
					<div class="room-icon">
						<div v-if="page.pax">
							<font-awesome-icon icon="fa-regular fa-user-group" size="1x" />
							<span>{{ page.pax }}</span>
						</div>
						<div v-if="page.surface">
							<font-awesome-icon icon="fa-regular fa-arrows-maximize" size="1x" />
							<span>{{ page.surface }}</span>
						</div>
					</div>
				</div>
				<div class="columns column6">
					<div v-parse-links v-html="page.content" />
					<p v-if="page.bookingID">
						<book-button :roomid="page.bookingID" class="button button-cta">
							{{ $t('bookThisRoom') }}
						</book-button>
					</p>
				</div>
				<slot />
			</div>
		</section>

		<section v-if="page.extraContent" class="main-content background-color align-left">
			<div class="row">
				<div class="columns column12">
					<div v-parse-links v-html="page.extraContent" />
				</div>
			</div>
		</section>

		<client-only>
			<carousel
				:per-page="1"
				:pagination-position="'bottom-overlay'"
				:autoplay="7000"
				:transition="1500"
				:pause-autoplay-on-hover="false"
				:wrap-around="true"
				class="slides"
			>
				<slide v-for="(item, index) in page.images" :key="index">
					<div class="slide">
						<picture>
							<source
								v-if="item.imagesWebp"
								:srcset="item.imagesWebp[1]"
								media="(max-width: 500px)"
								type="image/webp"
							/>
							<source v-if="item.imagesWebp" :srcset="item.imagesWebp[0]" type="image/webp" />
							<source :srcset="item.images[1]" media="(max-width: 500px)" />
							<source :srcset="item.images[0]" />
							<img :src="item.image" :alt="item.imageAlt" loading="lazy" />
						</picture>
					</div>
				</slide>

				<template #addons>
					<pagination />
				</template>
			</carousel>
		</client-only>

		<section class="main-content align-center">
			<div class="row">
				<div class="columns column12">
					<h2>{{ defaults[locale].rooms.header }}</h2>
				</div>
			</div>
			<div class="row promotions-row flex-row slide-row">
				<div
					v-for="room in roomsData.filter((item) => item.pageID !== page.pageID)"
					:key="room.pageID"
					class="block flex-item"
				>
					<div class="block-img">
						<nuxt-link :to="localePath(`/${room.filename}`)">
							<picture>
								<source
									v-if="room.imageOverviewWebp"
									:srcset="room.imageOverviewWebp"
									type="image/webp"
								/>
								<source :srcset="room.imageOverview" />
								<img :src="room.imageOverview" :alt="room.imageOverviewAlt" loading="lazy" />
							</picture>
						</nuxt-link>
					</div>
					<div class="block-content">
						<h3>{{ room.header }}</h3>
						<nuxt-link :to="localePath(`/${room.filename}`)" class="button button-no-fill">
							{{ defaults[locale].rooms.view }}
						</nuxt-link>
					</div>
				</div>
			</div>
		</section>
	</page-container>
</template>

<script setup>
const { page, fetchPage } = usePage();
const { defaults, fetchDefaults } = useDefaults();
const { locale } = useI18n();

await fetchPage();
await fetchDefaults();

const { data: roomsData } = await useWebsiteFetch('rooms', {
	query: { language: locale.value },
	key: `${locale.value}/rooms`,
});
</script>

<style lang="scss" scoped>
.main-content {
	:deep(ul) {
		padding: 0 0 0 20px;
	}

	:deep(li) {
		width: 50%;
		float: left;
		padding: 0 50px 0 0;
	}
}

.row.promotions-row {
	flex-flow: row wrap;
	padding: 0;
}

.block {
	width: 30%;
	margin: 1%;
	flex: auto;
}

.room-icon {
	display: flex;
	flex-flow: row wrap;
	align-items: center;
	justify-content: flex-start;
	gap: 20px;
	font-size: 16px;
	margin: 0 0 10px;

	svg,
	i {
		font-size: 13px;
		margin: 0 3px 0 0;
	}
}

.slides img {
	width: 100%;
}

.slides .slide {
	width: 100%;
}

/* stylelint-disable selector-class-pattern */
:deep(.carousel__pagination) {
	position: relative;
	margin-top: -50px;
}

:deep(.carousel__pagination-item) {
	margin: 0 4px;

	button {
		width: 11px;
		height: 11px;
		border-radius: 50%;
		background: rgb(239 239 239);

		&.carousel__pagination-button--active {
			background: #222;
		}

		&::after {
			display: none;
		}
	}
}
/* stylelint-enable selector-class-pattern */

@media (max-width: 1080px) {
	.row.promotions-row {
		flex-flow: row wrap;
		padding: 0 20px;
	}

	.block {
		width: 48%;
		flex: auto;
	}
}

@media (max-width: 780px) {
	.block {
		width: 98%;
		margin: 2% 1%;
		flex: auto;
	}
}
</style>
